import { useCallback, useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import styled from 'styled-components'

import { useModuleIds, useUserProjectRole } from 'portal/lib/hooks'
import {
  eventsCountLoad,
  eventsCountReload,
  eventsLoad,
  eventsReload
} from 'portal/store/events/actions'
import { SmallSpinner } from 'portal/lib/primitives'

const arr = []

export const useEventsLoader = (
  startDate,
  endDate,
  statuses = arr,
  sourceTypes = arr,
  pageSize = 100
) => {
  const dispatch = useDispatch()
  const { accountId, projectId, moduleName } = useModuleIds()

  const { isReadOnly } = useUserProjectRole(accountId, projectId)

  const reload = useCallback(() => {
    const filterSourceTypes =
      Array.isArray(sourceTypes) && sourceTypes.length > 0
        ? sourceTypes
        : ['ClientBulkUpdate', 'MassUpdate', 'Report', 'WasteRouteImport']

    const filter = {
      startDate: startDate,
      endDate: endDate,
      statuses,
      sourceTypes: filterSourceTypes
    }
    dispatch(
      eventsLoad(accountId, projectId, moduleName, pageSize, null, [], filter)
    )
    dispatch(eventsCountLoad(accountId, projectId, moduleName, filter))
  }, [
    sourceTypes,
    startDate,
    endDate,
    statuses,
    dispatch,
    accountId,
    projectId,
    moduleName,
    pageSize
  ])

  useEffect(() => {
    reload()
  }, [reload])

  const [isFocused, setIsFocused] = useState(true)
  const onFocus = () => {
    setIsFocused(true)
  }
  const onBlur = () => {
    setIsFocused(false)
  }
  useEffect(() => {
    window.addEventListener('focus', onFocus)
    window.addEventListener('blur', onBlur)
    onFocus()
    return () => {
      window.removeEventListener('focus', onFocus)
      window.removeEventListener('blur', onBlur)
    }
  }, [])

  const {
    loading,
    error,
    items,
    continuationToken,
    loadingCount,
    errorCount,
    count
  } = useSelector(state => {
    return state.events[moduleName]
  })

  function showTotalRecords() {
    if (loadingCount) {
      return (
        <SmallSpinnerWrapper>
          <SmallSpinner />
        </SmallSpinnerWrapper>
      )
    }
    if (errorCount !== null) {
      return 'Error fetching total events'
    }
    return (
      <ShowingRecordsText data-testid="totalInPage">
        Showing {items.length} out of {count} total event
        {count === 1 ? '' : 's'}
      </ShowingRecordsText>
    )
  }

  const silent_reload = useCallback(() => {
    const filterSourceTypes =
      Array.isArray(sourceTypes) && sourceTypes.length > 0
        ? sourceTypes
        : ['ClientBulkUpdate', 'MassUpdate', 'Report', 'WasteRouteImport']

    const filter = {
      startDate: startDate,
      endDate: endDate,
      statuses,
      sourceTypes: filterSourceTypes
    }
    dispatch(
      eventsReload(accountId, projectId, moduleName, pageSize, items, filter)
    )
    dispatch(eventsCountReload(accountId, projectId, moduleName, filter))
  }, [
    sourceTypes,
    startDate,
    endDate,
    statuses,
    dispatch,
    accountId,
    projectId,
    moduleName,
    pageSize,
    items
  ])

  useEffect(() => {
    const id = setInterval(() => {
      if (isFocused) {
        silent_reload()
      }
    }, 10000)
    return () => clearInterval(id)
  }, [isFocused, silent_reload])

  function handlePageChange() {
    if (continuationToken === null) {
      return
    }

    const filterSourceTypes =
      Array.isArray(sourceTypes) && sourceTypes.length > 0
        ? sourceTypes
        : ['ClientBulkUpdate', 'MassUpdate', 'Report']

    const filter = {
      startDate: startDate,
      endDate: endDate,
      statuses,
      sourceTypes: filterSourceTypes
    }
    dispatch(
      eventsLoad(
        accountId,
        projectId,
        moduleName,
        pageSize,
        continuationToken,
        items,
        filter
      )
    )
  }

  const hasNextPage = continuationToken != null

  return {
    loading,
    error,
    items,
    count,
    reload,
    handlePageChange,
    hasNextPage,
    showTotalRecords,
    readOnly: isReadOnly
  }
}

const ShowingRecordsText = styled.div`
  margin: 1rem 0.5rem 0;
  text-align: center;
`
const SmallSpinnerWrapper = styled.div`
  position: relative;
  margin-top: 0.5rem;
`
